import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { categories, getHotComics, getLatestComics, getComicsByCategory, features, stats } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Home() {
  const [activeTab, setActiveTab] = useState<string>('hanman');
  const [tabComics, setTabComics] = useState<Comic[]>([]);
  const hotComics = getHotComics(8);
  const latestComics = getLatestComics(6);

  useEffect(() => {
    setTabComics(getComicsByCategory(activeTab).slice(0, 8));
  }, [activeTab]);

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20manga%20and%20comic%20illustration%20background%20with%20warm%20orange%20red%20and%20soft%20pink%20gradient%20tones%2C%20subtle%20ink%20splash%20textures%2C%20minimalist%20Japanese%20and%20Korean%20comic%20art%20elements%2C%20clean%20modern%20composition%2C%20atmospheric%20lighting%2C%20no%20text%2C%20no%20characters%2C%20abstract%20artistic%20style&width=1920&height=1080&seq=hero-bg-manga&orientation=landscape"
            alt="虫虫动漫"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-4 tracking-wide">
              虫虫动漫
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              韩漫 · 日漫 · 国漫 分类热门推荐 | 免费在线阅读 | 下拉式滚动浏览体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="relative w-full sm:w-96">
                <input
                  type="text"
                  placeholder="搜索你喜欢的漫画..."
                  className="w-full pl-10 pr-4 py-3 rounded-full text-sm bg-white/15 border border-white/25 text-white placeholder-white/60 outline-none focus:bg-white/25 focus:border-white/40 transition-all"
                />
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-white/70"></i>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {categories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    className="px-5 py-2.5 rounded-full text-sm font-medium bg-white/15 border border-white/20 text-white hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {cat.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-4">
                  <div className="text-2xl md:text-3xl font-heading text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Tabs Section */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">漫画分类</h2>
              <p className="text-sm text-foreground-600">韩漫、日漫、国漫精选，分类清晰选漫无忧</p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="inline-flex bg-background-200/60 rounded-full px-1 py-1">
                {categories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => {
                      setActiveTab(cat.key);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeTab === cat.key
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-700 hover:text-foreground-900'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {tabComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to={`/category/${activeTab}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看更多{activeTab === 'hanman' ? '韩漫' : activeTab === 'riman' ? '日漫' : '国漫'}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">热门推荐</h2>
                <p className="text-sm text-foreground-600">千万读者都在看的热门漫画</p>
              </div>
              <Link
                to="/category/hanman"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </div>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">最新更新</h2>
                <div className="space-y-3">
                  {latestComics.map((comic, idx) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-background-200/50 transition-colors cursor-pointer group"
                    >
                      <span className={`text-2xl font-heading w-8 text-center flex-shrink-0 ${
                        idx < 3 ? 'text-primary-500' : 'text-foreground-400'
                      }`}>
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div className="w-12 h-16 rounded overflow-hidden flex-shrink-0">
                        <img
                          src={comic.coverImage}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                          {comic.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-0.5">{comic.latestChapter}</p>
                      </div>
                      <div className="flex-shrink-0 text-right hidden sm:block">
                        <span className="text-xs text-foreground-400">{comic.updateDate}</span>
                        <div className="text-xs text-foreground-500 mt-0.5">{comic.author}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="lg:w-80 flex-shrink-0">
                <div className="bg-background-50 rounded-lg p-5 sticky top-24">
                  <h3 className="text-lg font-heading text-foreground-900 mb-4">本周热门标签</h3>
                  <div className="flex flex-wrap gap-2">
                    {['恋爱', '奇幻', '战斗', '校园', '逆袭', '仙侠', '热血', '喜剧', '武侠', '冒险', '异能', '创业'].map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 cursor-pointer transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">为什么选择虫虫动漫？</h2>
              <p className="text-sm text-foreground-600">专注于为漫画爱好者提供最佳的在线阅读体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {features.map((feature) => (
                <div key={feature.title} className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className={`${feature.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20background%20with%20soft%20gradient%20of%20coral%20pink%20and%20light%20amber%20tones%2C%20minimalist%20composition%2C%20subtle%20paper%20texture%20overlay%2C%20gentle%20atmospheric%20lighting%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=1920&height=600&seq=cta-bg-manga&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary-900/70"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-white mb-3">立即开始你的漫画之旅</h2>
            <p className="text-white/80 text-sm md:text-base mb-6">海量韩漫、日漫、国漫等你来探索，完全免费，立即阅读！</p>
            <Link
              to="/category/hanman"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-primary-700 text-sm font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始阅读
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}